/**
 * Helpers for UserManagementInner to reduce cognitive complexity.
 */

import type React from 'react';
import type { BreadcrumbItem } from '../../components/layouts/PageHeader';
import authService from '../../auth/authService';
import userService from '../../services/userService';
import { mapUserToUpdateRequest } from '../../utils/userMapper';
import { getUserRoleFromAuthUser } from '../../utils/roleUtils';
import type { User, UserTableRow } from '../../types/user.types';

export function formatUserSpecialty(value: User['specialty']): string {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }
  return value || '';
}

export function resolveFacilityIdFromNav(locationState: unknown): string {
  if (typeof locationState !== 'object' || locationState === null) {
    return '';
  }
  const facilityId = (locationState as { facilityId?: string | number }).facilityId;
  if (facilityId != null && String(facilityId).trim()) {
    return String(facilityId).trim();
  }
  return '';
}

export function resolveFacilityNameFromNav(locationState: unknown): string {
  if (typeof locationState !== 'object' || locationState === null) {
    return '';
  }
  const facilityName = (locationState as { facilityName?: string }).facilityName;
  return facilityName != null && String(facilityName).trim() ? String(facilityName).trim() : '';
}

export function isFacilityScopedUsersList(facilityIdFromNav: string): boolean {
  if (!facilityIdFromNav) {
    return false;
  }
  return getUserRoleFromAuthUser(authService.getUser()) === 'SuperAdmin';
}

export function buildUserManagementBreadcrumbs(isFacilityScopedUsers: boolean): BreadcrumbItem[] {
  if (isFacilityScopedUsers) {
    return [
      { label: 'Home', path: '/' },
      { label: 'Facility Management', path: '/facilities' },
      { label: 'Users' },
    ];
  }
  return [
    { label: 'Home', path: '/' },
    { label: 'Users', path: '/users' },
    { label: 'User Management' },
  ];
}

export function resolveUsersListFacilityId(facilityIdFromNav: string): string | undefined {
  const currentUser = authService.getUser();
  const role = getUserRoleFromAuthUser(currentUser);
  if (facilityIdFromNav && role === 'SuperAdmin') {
    return facilityIdFromNav;
  }
  const isRisAdmin = currentUser?.roles?.includes('ris-admin') ?? false;
  const storedFacilityId = authService.getFacilityId();
  const facilityFromObject = authService.getFacility();
  const effectiveFacilityId =
    storedFacilityId || (facilityFromObject ? String(facilityFromObject.id) : undefined);
  if (isRisAdmin && effectiveFacilityId) {
    return effectiveFacilityId;
  }
  return undefined;
}

export function buildUsersListFilters(
  roleFilter: string,
  statusFilter: string,
  search: string
): { role?: string; status?: string; search?: string } | undefined {
  const filters: { role?: string; status?: string; search?: string } = {};
  if (roleFilter) {
    filters.role = roleFilter;
  }
  if (statusFilter) {
    filters.status = statusFilter;
  }
  if (search) {
    filters.search = search;
  }
  return Object.keys(filters).length > 0 ? filters : undefined;
}

type NotifyService =
  | {
      show: (opts: {
        title: string;
        message: string;
        type: 'success' | 'error' | 'info' | 'warning';
        duration?: number;
      }) => void;
    }
  | undefined;

export function notifyUserManagement(
  uiNotificationService: NotifyService,
  success: boolean,
  message: string
): void {
  if (!uiNotificationService) {
    return;
  }
  uiNotificationService.show({
    title: success ? 'Success' : 'Error',
    message,
    type: success ? 'success' : 'error',
    duration: success ? 3000 : 5000,
  });
}

export function computeSummaryStatsFromUsers(users: User[]): {
  total: number;
  active: number;
  radiologists: number;
  technicians: number;
  inactive: number;
} {
  const total = users.length;
  const active = users.filter(u => u.status === 'Active').length;
  const radiologists = users.filter(u => u.role === 'Radiologist' && u.status === 'Active').length;
  const technicians = users.filter(u => u.role === 'Technician' && u.status === 'Active').length;
  const inactive = users.filter(u => u.status === 'Inactive').length;
  return { total, active, radiologists, technicians, inactive };
}

export function resolveSummaryStats(
  summaryCounts: {
    total: number;
    radiologists: number;
    technicians: number;
    inactive: number;
    active: number;
  } | null,
  users: User[]
) {
  if (summaryCounts) {
    return summaryCounts;
  }
  return computeSummaryStatsFromUsers(users);
}

export async function toggleUserActiveStatus(opts: {
  users: User[];
  user: UserTableRow;
  checked: boolean;
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  uiNotificationService: NotifyService;
  fetchUsers: () => void;
}): Promise<void> {
  const originalUser = opts.users.find(u => u.id?.toString() === opts.user.id);

  if (!originalUser?.id) {
    notifyUserManagement(opts.uiNotificationService, false, 'User ID not found');
    return;
  }

  const newStatus: 'Active' | 'Inactive' = opts.checked ? 'Active' : 'Inactive';
  const userId = originalUser.id;

  opts.setUsers(prev => prev.map(u => (u.id === userId ? { ...u, status: newStatus } : u)));

  const updatedUser = { ...originalUser, status: newStatus };
  const updateRequest = mapUserToUpdateRequest(updatedUser);

  try {
    const response = await userService.updateUser(userId, updateRequest);
    if (response.success) {
      notifyUserManagement(
        opts.uiNotificationService,
        true,
        response.message || `User ${newStatus.toLowerCase()} successfully`
      );
      opts.fetchUsers();
      return;
    }
    opts.setUsers(prev =>
      prev.map(u => (u.id === userId ? { ...u, status: originalUser.status || 'Active' } : u))
    );
    notifyUserManagement(
      opts.uiNotificationService,
      false,
      response.error || 'Failed to update user status'
    );
  } catch (error) {
    opts.setUsers(prev =>
      prev.map(u => (u.id === userId ? { ...u, status: originalUser.status || 'Active' } : u))
    );
    notifyUserManagement(
      opts.uiNotificationService,
      false,
      error instanceof Error ? error.message : 'Failed to update user status'
    );
  }
}

export async function handleUserFormSuccessFlow(opts: {
  user: User;
  userFormDialogMode: 'add' | 'edit';
  returnToViewUserOnClose: User | null;
  uiNotificationService: NotifyService;
  fetchUsers: () => void;
  setUserFormDialogOpen: (v: boolean) => void;
  setUserFormDialogInitialUser: (u: User | null) => void;
  setSelectedUserForView: (u: User | null) => void;
  setViewUserDialogOpen: (v: boolean) => void;
  setReturnToViewUserOnClose: (u: User | null) => void;
}): Promise<void> {
  opts.fetchUsers();
  if (opts.userFormDialogMode === 'add') {
    notifyUserManagement(opts.uiNotificationService, true, 'User created successfully');
    opts.setUserFormDialogOpen(false);
    opts.setUserFormDialogInitialUser(null);
    return;
  }

  notifyUserManagement(opts.uiNotificationService, true, 'User updated successfully');
  opts.setUserFormDialogOpen(false);
  opts.setUserFormDialogInitialUser(null);
  if (!(opts.returnToViewUserOnClose && opts.user?.id)) {
    return;
  }
  try {
    const res = await userService.getUserById(opts.user.id);
    if (res.success && res.data) {
      opts.setSelectedUserForView(res.data);
    } else {
      opts.setSelectedUserForView(opts.user);
    }
  } catch {
    opts.setSelectedUserForView(opts.user);
  }
  opts.setViewUserDialogOpen(true);
  opts.setReturnToViewUserOnClose(null);
}

export function formatDepartmentForUserView(
  user: User,
  formatSpecialty: (value: User['specialty']) => string
): string {
  if (user.role === 'Technician') {
    return 'Radiology';
  }
  if (user.role === 'Radiologist') {
    return (
      formatSpecialty(user.specialty) ||
      (Array.isArray(user.department) ? user.department.join(', ') : user.department) ||
      '—'
    );
  }
  return Array.isArray(user.department) ? user.department.join(', ') : user.department || '—';
}

export function formatEmployeeIdForUserView(user: User): string {
  if (user.role === 'Radiologist') {
    return user.doctor_id || '—';
  }
  return user.employee_id || '—';
}

export function formatAvailabilityHoursForUserView(
  user: User,
  formatTimeForDisplay: (t: string | undefined | null) => string
): string {
  const start = formatTimeForDisplay(user.shift_start_at);
  const end = formatTimeForDisplay(user.shift_end_at);
  if (start && end) {
    return `${start} - ${end}`;
  }

  const avail = Array.isArray(user.availability_hours)
    ? user.availability_hours[0]
    : user.availability_hours;

  if (typeof avail === 'string') {
    return avail;
  }

  if (Array.isArray(user.availability_hours)) {
    return user.availability_hours.join(' - ');
  }

  return '—';
}
